import type { LayoutElement } from "@jod/schemas";
import { Button, FloorPlan, PageHeader, StatusChip } from "@jod/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useFloorLayout } from "../../features/floor-plan/use-floor-layout.ts";

export const Route = createFileRoute("/_app/floor")({
  component: FloorScreen,
});

const STATUS_TONE = {
  available: "success",
  occupied: "warning",
  reserved: "info",
  disabled: "neutral",
} as const;

function FloorScreen() {
  const navigate = useNavigate();
  const { layout, statusById, sessionByLayoutTableId, isLoading } = useFloorLayout();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = layout.elements.find((e) => e.id === selectedId) ?? null;
  const selectedSession =
    selectedId !== null ? (sessionByLayoutTableId.get(selectedId) ?? null) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Floor"
        subtitle={
          isLoading
            ? "Loading floor plan…"
            : "Live table status from table sessions — tap a table for detail."
        }
      />
      <div className="grid gap-4 lg:grid-cols-[1fr_18rem]">
        <div className="h-[68vh] min-h-[420px]">
          <FloorPlan
            layout={layout}
            statusById={statusById}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onElementClick={(id, el) => {
              if (el.kind === "table") setSelectedId(id);
            }}
          />
        </div>

        <aside className="space-y-4 rounded-[var(--radius)] border border-hairline bg-glass p-4">
          {selected && selected.kind === "table" ? (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display font-semibold text-base">
                    {selected.label ?? selected.code}
                  </h3>
                  <p className="text-muted-foreground text-xs">
                    {selected.code} · seats {selected.seats} · cap {selected.capacity}
                  </p>
                </div>
                <StatusChip tone={STATUS_TONE[statusById[selected.id] ?? "available"]}>
                  {statusById[selected.id] ?? "available"}
                </StatusChip>
              </div>
              {selectedSession ? (
                <Button
                  size="sm"
                  className="w-full"
                  onClick={() =>
                    navigate({
                      to: "/tables/$tableSessionId",
                      params: { tableSessionId: selectedSession.id },
                    })
                  }
                >
                  Open table session
                </Button>
              ) : (
                <p className="text-muted-foreground text-sm">
                  No active table session matches this layout code yet.
                </p>
              )}
            </>
          ) : selected ? (
            <TableDetailFallback el={selected} />
          ) : (
            <p className="text-muted-foreground text-sm">Tap a table to see live session status.</p>
          )}
        </aside>
      </div>
    </div>
  );
}

function TableDetailFallback({ el }: { el: LayoutElement }) {
  return (
    <div>
      <h3 className="font-display font-semibold text-base capitalize">{el.kind}</h3>
      <p className="text-muted-foreground text-sm">{el.label ?? "Venue fixture"}</p>
    </div>
  );
}
